import { useMemo } from 'react';
import dayjs from 'dayjs';
import { IntlShape, useIntl } from 'react-intl';

import {
  ReservationFlowSelectedPaymentMethod,
  ReservationFlowSelectedPaymentMethodNewCreditCard,
  ReservationFlowPaymentMethodListTicketBook,
  ReservationFlowTicketBookAvailableReservationCount,
} from 'src/interfaces/reservation_flow';
import { isExpiredDate } from 'src/state-machines/booking/functions/is-expired-date';
import { isRestrictedTicketBookReservationCount } from 'src/state-machines/booking/functions/is-restricted-ticket-book-reservation-count';
import { getIsLegacyValidationForTicketbookEnabledMerchant } from 'src/utils/getIsLegacyValidationForTicketbookEnabledMerchant';
import { getDayWithSuffix } from 'src/utils/intl';

export interface UseDisplayBehaviorParams {
  value?:
    | ReservationFlowSelectedPaymentMethod
    | ReservationFlowSelectedPaymentMethodNewCreditCard;
  today: string; // YYYY-MM-DD で表される今日の日付
  merchantTimeZone: string;
  paymentMethodsTicketBook: ReservationFlowPaymentMethodListTicketBook;
  ticketBookAvailableReservationCounts?: ReservationFlowTicketBookAvailableReservationCount[];
  selectedDate?: string; // 現時点では複数日程に対応しない
  merchantPublicId: string;
}

export interface UseDisplayBehaviorValue {
  items: {
    product: ReservationFlowPaymentMethodListTicketBook['products'][number];
    isSelected: boolean;
    isDisabled: boolean;
    isPurchasable: boolean;
    availableTicketBooks: {
      publicId: number;
      notes: { text: string; isWarning: boolean }[];
    }[];
    notes?: { text: string; isWarning: boolean }[];
  }[];
}

export const useDisplayBehavior: (
  params: UseDisplayBehaviorParams,
) => UseDisplayBehaviorValue = ({
  value,
  today,
  merchantTimeZone,
  paymentMethodsTicketBook,
  ticketBookAvailableReservationCounts,
  selectedDate,
  merchantPublicId,
}) => {
  const intl = useIntl();

  const items = useMemo<UseDisplayBehaviorValue['items']>(() => {
    // 購入済みの回数券のみを表示する
    const listedProducts = paymentMethodsTicketBook.products.filter(
      (product) => {
        return product.available_ticket_books.length > 0;
      },
    );

    return listedProducts.map((product) => {
      const isSelected = (() => {
        if (typeof value === 'undefined') {
          return false;
        }

        if (value.type !== 'ticket_book') {
          return false;
        }

        return product.public_id === value.product_public_id;
      })();

      // 日時選択と予約回数制限の取得が完了するまでは選択不可
      if (
        typeof selectedDate === 'undefined' ||
        typeof ticketBookAvailableReservationCounts === 'undefined'
      ) {
        return {
          product,
          isSelected,
          isDisabled: true,
          isPurchasable: false,
          availableTicketBooks: product.available_ticket_books.map(
            (availableTicketBook) => {
              return {
                publicId: availableTicketBook.public_id,
                notes: getAvailableTicketBookNotes(
                  {
                    merchantTimeZone,
                    availableTicketBook,
                  },
                  intl,
                ),
              };
            },
          ),
        };
      }

      // 選択された日付の時点で有効な availableTicketBook を抽出
      // 特別対応マーチャントの場合は有効期限を見ない
      // issue: https://github.com/heyinc/rsv-issues/issues/21754
      const isLegacyValidationForTicketbook =
        getIsLegacyValidationForTicketbookEnabledMerchant(merchantPublicId);
      const availableTicketBooksWithinTheValidPeriod =
        isLegacyValidationForTicketbook
          ? product.available_ticket_books
          : product.available_ticket_books.filter((availableTicketBook) => {
              return !isExpiredDate({
                merchantTimeZone,
                baseDate: selectedDate,
                date: availableTicketBook.expires_at,
              });
            });

      // 有効な availableTicketBook がない場合は選択不可
      if (availableTicketBooksWithinTheValidPeriod.length === 0) {
        return {
          product,
          isSelected,
          isDisabled: true,
          isPurchasable: false,
          availableTicketBooks: product.available_ticket_books.map(
            (availableTicketBook) => {
              return {
                publicId: availableTicketBook.public_id,
                notes: getAvailableTicketBookNotes(
                  {
                    merchantTimeZone,
                    availableTicketBook,
                  },
                  intl,
                ),
              };
            },
          ),
          notes: [
            {
              text: intl.formatMessage({
                defaultMessage:
                  '有効期限を超えた日付が選択されているため利用できません',
                id: 'zXBq+9',
                description:
                  'src/templates/merchants/resources/book/components/payment-method/TicketBookItem/hooks/useDisplayBehavior/index.ts',
              }),
              isWarning: true,
            },
          ],
        };
      }

      // 選択された日付の時点で有効な availableTicketBook に予約回数制限が存在するかの確認
      const isRestricted = availableTicketBooksWithinTheValidPeriod.some(
        (availableTicketBook) => {
          const availableReservationCount =
            ticketBookAvailableReservationCounts.find((count) => {
              return count.publicId === availableTicketBook.public_id;
            });

          // 対応する予約回数制限が見つからない場合は制限されているみなす
          if (typeof availableReservationCount === 'undefined') {
            return true;
          }

          return isRestrictedTicketBookReservationCount(
            availableReservationCount,
          );
        },
      );

      // availableTicketBook に一つでも予約回数制限がある場合は選択できない
      if (isRestricted) {
        return {
          product,
          isSelected,
          isDisabled: true,
          isPurchasable: false,
          availableTicketBooks: product.available_ticket_books.map(
            (availableTicketBook) => {
              return {
                publicId: availableTicketBook.public_id,
                notes: getAvailableTicketBookNotes(
                  {
                    merchantTimeZone,
                    availableTicketBook,
                    availableReservationCounts:
                      ticketBookAvailableReservationCounts,
                    selectedDate,
                  },
                  intl,
                ),
              };
            },
          ),
        };
      }

      // 有効な availableTicketBook の合計残回数の算出
      const totalRemainCount = availableTicketBooksWithinTheValidPeriod.reduce(
        (acc, current) => {
          return acc + current.remain_count;
        },
        0,
      );

      // 消費回数が合計残回数を超える場合は選択不可
      if (product.consumption > totalRemainCount) {
        return {
          product,
          isSelected,
          isDisabled: true,
          isPurchasable: (() => {
            if (!product.is_published) {
              return false;
            }

            // パスワード保護されている場合、リスト表示可能でない場合は購入不可
            if (
              product.is_password_protected &&
              !product.is_password_protection_listable
            ) {
              return false;
            }

            // 今日購入した場合に、選択した日付が有効期限内である場合は購入可能
            const expiredAt = dayjs
              .tz(today, 'YYYY-MM-DD', merchantTimeZone)
              .add(product.expiration_month, 'month')
              .format('YYYY-MM-DD');

            return !isExpiredDate({
              merchantTimeZone,
              baseDate: selectedDate,
              date: expiredAt,
            });
          })(),
          availableTicketBooks: product.available_ticket_books.map(
            (availableTicketBook) => {
              return {
                publicId: availableTicketBook.public_id,
                notes: getAvailableTicketBookNotes(
                  {
                    merchantTimeZone,
                    availableTicketBook,
                    availableReservationCounts:
                      ticketBookAvailableReservationCounts,
                    selectedDate,
                  },
                  intl,
                ),
              };
            },
          ),
          notes: [
            {
              text: intl.formatMessage({
                defaultMessage: '残回数が足りないため利用できません',
                id: 'YVnzg5',
                description:
                  'src/templates/merchants/resources/book/components/payment-method/TicketBookItem/hooks/useDisplayBehavior/index.ts',
              }),
              isWarning: true,
            },
          ],
        };
      }

      return {
        product,
        isSelected,
        isDisabled: false,
        isPurchasable: false,
        availableTicketBooks: product.available_ticket_books.map(
          (availableTicketBook) => {
            return {
              publicId: availableTicketBook.public_id,
              notes: getAvailableTicketBookNotes(
                {
                  merchantTimeZone,
                  availableTicketBook,
                  availableReservationCounts:
                    ticketBookAvailableReservationCounts,
                  selectedDate,
                },
                intl,
              ),
            };
          },
        ),
      };
    });
  }, [
    value,
    today,
    merchantTimeZone,
    paymentMethodsTicketBook,
    ticketBookAvailableReservationCounts,
    selectedDate,
    intl,
    merchantPublicId,
  ]);

  return {
    items,
  };
};

export const getAvailableTicketBookNotes: (
  params: {
    merchantTimeZone: string;
    availableTicketBook: ReservationFlowPaymentMethodListTicketBook['products'][number]['available_ticket_books'][number];
    availableReservationCounts?: ReservationFlowTicketBookAvailableReservationCount[];
    selectedDate?: string;
  },
  intl: IntlShape,
) => { text: string; isWarning: boolean }[] = (
  {
    merchantTimeZone,
    availableTicketBook,
    availableReservationCounts,
    selectedDate,
  },
  intl,
) => {
  // 日付と利用回数制限が与えられない場合は基本情報のみを返す
  if (
    typeof availableReservationCounts === 'undefined' ||
    typeof selectedDate === 'undefined'
  ) {
    return getAvailableTicketBookInfoNotes(
      {
        merchantTimeZone,
        availableTicketBook,
      },
      intl,
    );
  }

  const availableReservationCount = availableReservationCounts.find((count) => {
    return count.publicId === availableTicketBook.public_id;
  });

  // 対応する利用回数制限が見つからない場合はエラーメッセージを含める
  if (typeof availableReservationCount === 'undefined') {
    return [
      ...getAvailableTicketBookInfoNotes(
        {
          merchantTimeZone,
          availableTicketBook,
        },
        intl,
      ),
      {
        text: intl.formatMessage({
          defaultMessage: 'この回数券は利用できません',
          id: 'pDEXOn',
          description:
            'src/templates/merchants/resources/book/components/payment-method/TicketBookItem/hooks/useDisplayBehavior/index.ts',
        }),
        isWarning: true,
      },
    ];
  }

  return [
    ...getAvailableTicketBookInfoNotes(
      {
        merchantTimeZone,
        availableTicketBook,
      },
      intl,
    ),
    getWeeklyNote(
      {
        merchantTimeZone,
        count: availableReservationCount.value.weekly,
        selectedDate,
      },
      intl,
    ),
    getDailyNote(
      {
        merchantTimeZone,
        count: availableReservationCount.value.daily,
        selectedDate,
      },
      intl,
    ),
    getUpcomingNote(
      {
        count: availableReservationCount.value.upcoming,
      },
      intl,
    ),
  ].filter(Boolean) as { text: string; isWarning: boolean }[];
};

export const getAvailableTicketBookInfoNotes: (
  params: {
    merchantTimeZone: string;
    availableTicketBook: ReservationFlowPaymentMethodListTicketBook['products'][number]['available_ticket_books'][number];
  },
  intl: IntlShape,
) => { text: string; isWarning: boolean }[] = (
  { merchantTimeZone, availableTicketBook },
  intl,
) => {
  return [
    {
      text: intl.formatMessage(
        {
          defaultMessage: '有効期限：{expiresAt}',
          id: 'uWWOn+',
          description:
            'src/templates/merchants/resources/book/components/payment-method/TicketBookItem/hooks/useDisplayBehavior/index.ts',
        },
        {
          expiresAt: dayjs
            .tz(availableTicketBook.expires_at, 'YYYY-MM-DD', merchantTimeZone)
            .locale(intl.locale)
            .format('ll'),
        },
      ),
      isWarning: false,
    },
    {
      text: intl.formatMessage(
        {
          defaultMessage: '残回数：{remainCount}',
          id: 'WxgX+E',
          description:
            'src/templates/merchants/resources/book/components/payment-method/TicketBookItem/hooks/useDisplayBehavior/index.ts',
        },
        {
          remainCount: availableTicketBook.remain_count,
        },
      ),
      isWarning: false,
    },
  ];
};

export const getWeeklyNote: (
  params: {
    merchantTimeZone: string;
    count: number | null;
    selectedDate: string;
  },
  intl: IntlShape,
) => { text: string; isWarning: boolean } | undefined = (
  { merchantTimeZone, count, selectedDate },
  intl,
) => {
  if (count === null) {
    return undefined;
  }

  const startDay = dayjs
    .tz(selectedDate, 'YYYY-MM-DD', merchantTimeZone)
    .startOf('isoWeek');
  const endDay = dayjs
    .tz(selectedDate, 'YYYY-MM-DD', merchantTimeZone)
    .endOf('isoWeek');

  const isNextMondayIsNextMonth = startDay.get('month') !== endDay.get('month');

  const dateText = `${intl.formatDate(startDay.toDate(), {
    timeZone: merchantTimeZone,
    dateStyle: 'long',
  })}~${
    isNextMondayIsNextMonth
      ? intl.formatDate(endDay.toDate(), {
          timeZone: merchantTimeZone,
          month: 'long',
          day: 'numeric',
        })
      : getDayWithSuffix(endDay.get('date'), intl)
  }`;

  if (count === 0) {
    return {
      text: intl.formatMessage(
        {
          defaultMessage:
            '当週（{dateText}）：予約回数の上限に達したため利用できません',
          id: '3m0qXF',
          description:
            'src/templates/merchants/resources/book/components/payment-method/TicketBookItem/hooks/useDisplayBehavior/index.ts',
        },
        {
          dateText,
        },
      ),
      isWarning: true,
    };
  }

  return {
    text: intl.formatMessage(
      {
        defaultMessage: '当週（{dateText}）：あと{count}回',
        id: 'nYYJ9m',
        description:
          'src/templates/merchants/resources/book/components/payment-method/TicketBookItem/hooks/useDisplayBehavior/index.ts',
      },
      {
        dateText,
        count,
      },
    ),
    isWarning: false,
  };
};

export const getDailyNote: (
  params: {
    merchantTimeZone: string;
    count: number | null;
    selectedDate: string;
  },
  intl: IntlShape,
) => { text: string; isWarning: boolean } | undefined = (
  { merchantTimeZone, count, selectedDate },
  intl,
) => {
  if (count === null) {
    return undefined;
  }

  const dateText = intl.formatDate(selectedDate, {
    timeZone: merchantTimeZone,
    month: 'long',
    day: 'numeric',
  });

  if (count === 0) {
    return {
      text: intl.formatMessage(
        {
          defaultMessage:
            '当日（{dateText}）：予約回数の上限に達したため利用できません',
          id: 'KFz5iq',
          description:
            'src/templates/merchants/resources/book/components/payment-method/TicketBookItem/hooks/useDisplayBehavior/index.ts',
        },
        {
          dateText,
        },
      ),
      isWarning: true,
    };
  }

  return {
    text: intl.formatMessage(
      {
        defaultMessage: '当日（{dateText}）：あと{count}回',
        id: 'ITCr9V',
        description:
          'src/templates/merchants/resources/book/components/payment-method/TicketBookItem/hooks/useDisplayBehavior/index.ts',
      },
      {
        dateText,
        count,
      },
    ),
    isWarning: false,
  };
};

export const getUpcomingNote: (
  params: {
    count: number | null;
  },
  intl: IntlShape,
) => { text: string; isWarning: boolean } | undefined = ({ count }, intl) => {
  if (count === null) {
    return undefined;
  }

  if (count === 0) {
    return {
      text: intl.formatMessage({
        defaultMessage:
          '同時に持てる予約数：上限に達しているため利用できません',
        id: '8DchJV',
        description:
          'src/templates/merchants/resources/book/components/payment-method/TicketBookItem/hooks/useDisplayBehavior/index.ts',
      }),
      isWarning: true,
    };
  }

  return {
    text: intl.formatMessage(
      {
        defaultMessage: '同時に持てる予約数：あと{count}回',
        id: '8hPWH/',
        description:
          'src/templates/merchants/resources/book/components/payment-method/TicketBookItem/hooks/useDisplayBehavior/index.ts',
      },
      {
        count,
      },
    ),
    isWarning: false,
  };
};
